<template>
  <section class="mozio-app">
    <div
      v-if="computedShowMozio"
      class="moz-search-form"
    >
      <div class="moz-search-form__text">
        <div class="moz-search-form__title">
          {{ 'SEARCH_TITLE' | i18n(locale) }}
        </div>
        <div
          class="moz-search-form__subtitle"
          v-html="searchSubtitle"
        />
      </div>
      <div class="moz-search-form__forms">
        <div class="moz-form-groups">
          <div
            v-if="isAllowedPlaceFromTypeId"
            class="moz-search-form__row"
          >
            <moz-form-group>
              <template slot="label">
                {{ 'INPUT_LABEL_FROM' | i18n(locale) }}
              </template>
              <moz-input
                id="place-from"
                v-model="inputValueFrom"
                name="place-from"
                :placeholder="placeFromPlaceholder"
              />
            </moz-form-group>
            <moz-search-btn
              v-if="!isAllowedPlaceToTypeId"
              class="moz-search-form__submit"
              :disabled="!inputsNotEmpty"
              @click="onSearchBtnClick"
            >
              {{ 'MOZIO_SEARCH_BUTTON' | i18n(locale) }}
            </moz-search-btn>
          </div>
          <div
            v-if="isAllowedPlaceToTypeId"
            class="moz-search-form__row"
          >
            <moz-form-group>
              <template slot="label">
                {{ 'INPUT_LABEL_TO' | i18n(locale) }}
              </template>
              <moz-input
                id="place-to"
                v-model="inputValueTo"
                name="place-to"
                :placeholder="placeToPlaceholder"
              />
            </moz-form-group>
            <moz-search-btn
              class="moz-search-form__submit"
              :disabled="!inputsNotEmpty"
              @click="onSearchBtnClick"
            >
              {{ 'MOZIO_SEARCH_BUTTON' | i18n(locale) }}
            </moz-search-btn>
          </div>
        </div>
      </div>
    </div>
    <slot
      v-if="showMozio"
      :updated-config="updatedConfig"
      :events="events"
    />
  </section>
</template>

<script>
import { i18n as i18nFilter } from '../Mozio/vueFilters';
import { MODES } from '../Mozio/constants';

import MozFormGroup from './components/MozFormGroup';
import MozInput from './components/MozInput';
import MozSearchBtn from './components/MozSearchBtn';
import { delay } from '../utils/functions';
import { smoothScrollTo } from '../utils/smoothScrollTo';
import { GTM_EVENTS, sendGTM } from '../utils/gtm';

/**
 * Список ID точек для которых нужна форма поиска
 * @type {{DISTRICT: number, ISLAND: number, CITY: number, SIGHT: number}}
 * 1 - Город
 * 6 - Остров
 * 7 - Достопримечательности
 * 8 - Район
 */
const AVAILABLE_PLACES = {
  CITY: 1,
  ISLAND: 6,
  SIGHT: 7,
  DISTRICT: 8,
};
const AVAILABLE_PLACE_TYPE_IDS = [
  AVAILABLE_PLACES.CITY,
  AVAILABLE_PLACES.ISLAND,
  AVAILABLE_PLACES.SIGHT,
  AVAILABLE_PLACES.DISTRICT,
];

export default {
  name: 'MozioSearchForm',
  components: {
    MozFormGroup,
    MozInput,
    MozSearchBtn,
  },
  filters: {
    i18n: i18nFilter,
  },
  props: {
    from: {
      type: String,
      default: '',
    },
    to: {
      type: String,
      default: '',
    },
    currency: {
      type: String,
      default: '',
    },
    passengers: {
      type: Number,
      default: 1,
    },
    mode: {
      type: String,
      default: MODES.NO_RESULTS,
    },
    locale: {
      type: String,
      default: 'ru',
    },
    placeFromTypeId: {
      type: [Number, String],
      default: 1,
    },
    placeToTypeId: {
      type: [Number, String],
      default: 1,
    },
    /**
     * Используется ли Google api при поиске
     * 1 - да, 0 - нет
     */
    routeFalsity: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return {
      inputValueFrom: '',
      inputValueTo: '',
      showMozio: false,
    };
  },
  computed: {
    isAllowedPlaceFromTypeId() {
      return AVAILABLE_PLACE_TYPE_IDS.includes(this.placeFromTypeId);
    },
    isAllowedPlaceToTypeId() {
      return AVAILABLE_PLACE_TYPE_IDS.includes(this.placeToTypeId);
    },
    isAnyPlaceTypeIdAllowed() {
      return this.isAllowedPlaceFromTypeId || this.isAllowedPlaceToTypeId;
    },
    onlyOnePlaceTypeAllowed() {
      return (this.isAllowedPlaceFromTypeId && !this.isAllowedPlaceToTypeId)
        || (!this.isAllowedPlaceFromTypeId && this.isAllowedPlaceToTypeId);
    },
    notShowMozio() {
      return !!(this.onlyOnePlaceTypeAllowed && this.routeFalsity);
    },
    computedShowMozio() {
      return this.isAnyPlaceTypeIdAllowed && !this.notShowMozio;
    },
    placeFromNotEmpty() {
      return this.isAllowedPlaceFromTypeId ? this.inputValueFrom.length >= 2 : true;
    },
    placeToNotEmpty() {
      return this.isAllowedPlaceToTypeId ? this.inputValueTo.length >= 2 : true;
    },
    inputsNotEmpty() {
      return this.placeFromNotEmpty && this.placeToNotEmpty;
    },
    searchSubtitle() {
      let key = '';

      switch (this.mode) {
        case MODES.NO_SALES:
        case MODES.NO_CAPACITY:
          if (this.isAllowedPlaceFromTypeId && this.isAllowedPlaceToTypeId) {
            key = 'SEARCH_SUBTITLE_NO_SALES_OR_CAPACITY';
          } else if (this.isAllowedPlaceFromTypeId) {
            key = 'SEARCH_SUBTITLE_FROM_NO_SALES_OR_CAPACITY';
          } else {
            key = 'SEARCH_SUBTITLE_TO_NO_SALES_OR_CAPACITY';
          }
          break;

        default:
          if (this.isAllowedPlaceFromTypeId && this.isAllowedPlaceToTypeId) {
            key = 'SEARCH_SUBTITLE';
          } else if (this.isAllowedPlaceFromTypeId) {
            key = 'SEARCH_SUBTITLE_FROM';
          } else {
            key = 'SEARCH_SUBTITLE_TO';
          }
      }

      return this.$options.filters.i18n(key, this.locale);
    },
    placeFromPlaceholder() {
      return this.$options.filters.i18n('INPUT_PLACEHOLDER_FROM', this.locale);
    },
    placeToPlaceholder() {
      return this.$options.filters.i18n('INPUT_PLACEHOLDER_TO', this.locale);
    },
    updatedConfig() {
      const {
        from,
        to,
        currency,
        passengers,
        mode,
        locale,
      } = this.$props;
      const decoratedFrom = `${from} ${this.inputValueFrom}`;
      const decoratedTo = `${to} ${this.inputValueTo}`;

      return {
        from: decoratedFrom.trim(),
        to: decoratedTo.trim(),
        currency,
        passengers,
        mode,
        locale,
        clarifiedSearch: this.computedShowMozio,
      };
    },
    events() {
      return {
        onEmptyResult(reason) {
          console.log(reason);
        },
      };
    },
  },
  mounted() {
    if (!this.computedShowMozio) {
      this.showMozio = true;
    } else {
      sendGTM(GTM_EVENTS.MOZIO_FORM_SHOW);
    }
  },
  methods: {
    showMozioFunction() {
      this.showMozio = true;
    },
    hideMozio() {
      this.showMozio = false;
    },
    toggleMozio() {
      this.hideMozio();
      return delay(500).then(this.showMozioFunction);
    },
    async onSearchBtnClick() {
      await this.toggleMozio();
      smoothScrollTo(document.querySelector('.mozio'), 72);
    },
  },
};
</script>

<style lang="scss" scoped>
.mozio-app {
  margin: 46px 0;
}

.moz-search-form {
  padding-bottom: 20px;

  &__text {
    padding: 32px 16px;
    border: solid 1px #e4e7ea;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    background-color: #fff;

    @media screen and (min-width: $tablet-width) {
      padding: 48px 32px 40px;
    }

    @media screen and (min-width: $desktop-width) {
      padding: 48px 40px;
    }
  }

  &__title {
    margin-bottom: 24px;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.38;

    @media screen and (min-width: $tablet-width) {
      font-size: 26px;
    }
  }

  &__subtitle {
    font-size: 14px;
    line-height: 1.75;

    @media screen and (min-width: $tablet-width) {
      font-size: 16px;
    }
  }

  &__forms {
    padding: 27px 16px 32px;
    background-color: #468cc8;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;

    @media screen and (min-width: $tablet-width) {
      padding: 24px 32px 46px;
    }

    @media screen and (min-width: $desktop-width) {
      padding: 33px 40px 48px;
    }
  }

  &__row {
    margin-bottom: 11px;

    &:last-child {
      margin-bottom: 0;
    }

    @media screen and (min-width: $tablet-width) {
      display: flex;
      align-items: flex-end;
      margin-bottom: 32px;
    }
  }

  &__submit {
    display: block;
    width: 100%;
    margin-top: 16px;

    @media screen and (min-width: $tablet-width) {
      display: inline-block;
      flex: 0 0 auto;
      width: auto;
      margin-top: 0;
      margin-left: 16px;
    }
  }
}
</style>
